import type { SupabaseClient } from '@supabase/supabase-js';
import { env } from '@/config/env';

/**
 * Browser Supabase client (anon key + user session). Every query is subject
 * to Row Level Security.
 *
 * `@supabase/supabase-js` (~58 KB gzip) is dynamically imported so it never
 * enters the public landing page's initial JS graph (visual-experience-spec
 * §J, PERF-1): the chunk is fetched only the first time a caller actually
 * needs the client, not as part of the app shell's static import graph.
 * `getSupabase()`/`requireSupabase()` are therefore async; every call site
 * already lives inside an async function (a service call or an effect), so
 * this is `await`ed, not a behavior change.
 */
let client: SupabaseClient | null = null;
let clientPromise: Promise<SupabaseClient> | null = null;

async function loadClient(): Promise<SupabaseClient> {
  const { createClient } = await import('@supabase/supabase-js');
  const created = createClient(env.supabaseUrl, env.supabaseAnonKey, {
    auth: {
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
    global: {
      headers: { 'x-client-info': 'al-qinaa-web' },
    },
  });
  client = created;
  return created;
}

/**
 * Resolves to the Supabase client, or null when the environment is not
 * configured, so the public experience still renders and shows a clear
 * notice instead of failing silently.
 */
export function getSupabase(): Promise<SupabaseClient | null> {
  if (!env.isSupabaseConfigured) return Promise.resolve(null);
  if (client) return Promise.resolve(client);
  clientPromise ??= loadClient();
  return clientPromise;
}

export class BackendNotConfiguredError extends Error {
  constructor() {
    super('Supabase is not configured for this environment (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY).');
    this.name = 'BackendNotConfiguredError';
  }
}

/** Resolves to the client or throws a typed error that the UI renders as a notice. */
export async function requireSupabase(): Promise<SupabaseClient> {
  const c = await getSupabase();
  if (!c) throw new BackendNotConfiguredError();
  return c;
}
